import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FirstPage, LastPage } from "@mui/icons-material";
import dayjs from "dayjs";
import { storageLeadList } from "../storageStaticData";
import CustomButton from "../../../../components/common/button/CustomButton";
import ManageStorage from "./ManageStorage";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllStorageAction,
  getSingleStorageAction,
  setStorageResponse,
} from "../../../../store/reducers/storageSlice";
import { setAddNewLeadAction, setUpdateLeadAction } from "../../../../store/reducers/leadSlice";

/* ─── color helper for storage statuses ─── */
const getColor = (status: string): string => {
  switch (status) {
    case "STORAGE_ENQUIRY":
      return "#FF66C4"; // pink
    case "STORAGE":
      return "#D16B2C"; // brown / orange
    case "COMPLETE":
      return "#7ED957"; // green
    default:
      return "#000000";
  }
};

/* ─── Single card component ─── */
const StorageLeadListCard: React.FC<{
  lead: any;
  selectedLead: string | null;
  onSelect: (id: string) => void;
}> = ({ lead, selectedLead, onSelect }) => {
  const dateData = [
    {
      label: "F",
      fullLabel: "Follow Up Date",
      date: lead?.follow_up_date,
      color: "#FF4C4C",
    },
    {
      label: "A",
      fullLabel: "Created At",
      date: lead?.created_at,
      color: "#339DFF",
    },
    {
      label: "M",
      fullLabel: "Moving On Date",
      date: lead?.moving_on_date,
      color: "#2DBE60",
    },
    {
      label: "L",
      fullLabel: "Last Updated",
      date: lead?.updated_at,
      color: "#FF934F",
    },
  ];

  const formatShortDate = (dateString: string) => {
    if (!dateString) return "--";
    return dayjs(dateString).format("DD\nMMM").toUpperCase();
  };

  const formatFullDate = (dateString: string) => {
    if (!dateString) return "--";
    return dayjs(dateString).format("DD MMMM YYYY");
  };


  return (
    <Paper
      elevation={1}
      sx={{
        border: `1px solid ${
          selectedLead === lead.leadId ? getColor(lead?.status) : "#ddd"
        }`,
        display: "flex",
        alignItems: "center",
        p: "2px",
        backgroundColor:
          selectedLead === lead?.leadId
            ? alpha(getColor(lead?.status), 0.15)
            : "#fff",
        position: "relative",
        overflow: "hidden",
        marginBottom: "4px",
        maxHeight: "100vh",
        cursor: "pointer",
      }}
      onClick={() => onSelect(lead?.leadId)}
    >
      {/* Left Accent */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 10,
          backgroundColor: getColor(lead?.status),
        }}
      />

      {/* Content */}
      <Box sx={{ ml: 2, width: "100%" }}>
        <Grid container spacing={0}>
          <Grid item xs={5.5}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ lineHeight: 1.5, textTransform: "capitalize" }}
            >
              {lead?.customerName}
            </Typography>
          </Grid>

          {dateData.map((item, index) => (
            <Grid
              key={index}
              item
              xs
              sx={{
                padding: "2px 4px",
                textAlign: "center",
                display: "grid",
              }}
            >
              <Tooltip
                title={`${item.fullLabel} - ${formatFullDate(item.date)}`}
                arrow
              >
                <Typography
                  variant="body2"
                  align="center"
                  fontWeight={600}
                  sx={{
                    color: item.color,
                    fontSize: "12px",
                    display: "table-caption",
                  }}
                >
                  {item?.date ? formatShortDate(item.date) : "  -"}
                </Typography>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );
};

/* ─── Main list component ─── */
interface StorageLeadListProps {
  setGenerateId: (id: string | null) => void;
}

const StorageLeadList: React.FC<StorageLeadListProps> = ({ setGenerateId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isScreenLarge = useMediaQuery("(max-width:1248px)");
  const { storageList, storagePagination, response } = useSelector(
    (state: any) => state.storage,
  );
  const [selectedLead, setSelectedLead] = useState<string | null>(
    storageList[0]?.leadId || null,
  );
  console.log("storageList", storageList);
  /* ─── ManageStorage popup state ─── */
  const [manageStorageOpen, setManageStorageOpen] = useState(false);
  const [manageStorageTitle, setManageStorageTitle] = useState("");

  // Initialize with first lead selected
  useEffect(() => {
    if (storageList.length > 0 && !selectedLead) {
      setGenerateId(storageList[0].leadId);
      setSelectedLead(storageList[0].leadId);
    }
  }, [storageList,selectedLead]);

  const handleSelect = (id: string) => {
    setGenerateId(id);
    setSelectedLead(id);
  };

  useEffect(() => {
    dispatch(getAllStorageAction());
  }, [dispatch]);

  useEffect(() => {
    if (response?.addStorage || response?.updateStorage) {
      dispatch(setUpdateLeadAction(false));
      dispatch(getAllStorageAction());
      if (selectedLead) {
        dispatch(getSingleStorageAction({ storageId: selectedLead }));
      }
      dispatch(setAddNewLeadAction(false));
      dispatch(setStorageResponse(false));
      setManageStorageOpen(false);
    }
  }, [response?.addStorage, response?.updateStorage, dispatch, selectedLead]);

  return (
    <Box sx={{ flexDirection: "column" }}>
      {/* ─── ManageStorage Popup ─── */}
      {manageStorageOpen && (
        <ManageStorage
          selectLead={selectedLead}
          open={manageStorageOpen}
          onHide={() => setManageStorageOpen(false)}
          title={manageStorageTitle}
          screen="storage"
        />
      )}

      {/* Top Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "9px",
          background: "#fff",
          border: "1px solid #C4CDD5",
        }}
      >
        <CustomButton
          style={{ fontSize: "12px", padding: "4px 8px" }}
          title="New Storage Lead"
          onClick={() => {
            setManageStorageTitle("Add New Storage");
            setManageStorageOpen(true);
          }}
        />
        <CustomButton
          style={{ fontSize: "12px", padding: "4px 8px" }}
          title="Filter"
          variant="outlined"
          onClick={() => {}}
        />
      </Box>

      {/* List Header */}
      {storageList.length > 0 ? (
        <Box sx={{ width: "100%", paddingBottom: 4, paddingTop: 1 }}>
          <Grid
            container
            spacing={0}
            alignItems="center"
            sx={{
              background: "#E5F4F1",
              color: "#637381",
              padding: "10px 10px",
              marginBottom: "4px",
              border: "1px solid #C4CDD5",
            }}
          >
            <Grid item xs={6} sx={{ borderRight: "1px solid #C4CDD5" }}>
              <Typography variant="body2" fontWeight={600}>
                Customer List
              </Typography>
            </Grid>
            <Grid
              item
              xs
              sx={{
                textAlign: "center",
                borderRight: "1px solid #C4CDD5",
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                F
              </Typography>
            </Grid>
            <Grid
              item
              xs
              sx={{
                textAlign: "center",
                borderRight: "1px solid #C4CDD5",
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                A
              </Typography>
            </Grid>
            <Grid
              item
              xs
              sx={{
                textAlign: "center",
                borderRight: "1px solid #C4CDD5",
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                M
              </Typography>
            </Grid>
            <Grid item xs sx={{ textAlign: "center" }}>
              <Typography variant="body2" fontWeight={600}>
                L
              </Typography>
            </Grid>
          </Grid>

          {/* Lead Cards */}
          {storageList.map((lead: any) => (
            <StorageLeadListCard
              key={lead.leadId}
              lead={lead}
              selectedLead={selectedLead}
              onSelect={handleSelect}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            padding: "2px 10px",
            height: "60vh",
            display: "flex",
            textAlign: "center",
            alignItems: "center",
            color: "#4A4A4A",
            justifyContent: "center",
          }}
        >
          No storage leads available
        </Box>
      )}

      {/* Pagination */}
      {storageLeadList.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: 2,
            alignItems: "center",
            borderTop: "2px solid #d5d5d5",
          }}
        >
          <Button
            size="small"
            variant="contained"
            color="secondary"
            sx={{ padding: "0px", height: "20px", minWidth: "30px" }}
            disabled
          >
            <FirstPage sx={{ fontSize: "16px" }} />
          </Button>
          <Typography
            sx={{
              fontSize:
                isMdUp && !isScreenLarge
                  ? "13px"
                  : isScreenLarge
                    ? "12px"
                    : "12px",
              textAlign: "center",
            }}
          >
            {storageLeadList.length} listings found. Page 1 of 1.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            sx={{ padding: "0px", height: "20px", minWidth: "30px" }}
            disabled
          >
            <LastPage sx={{ fontSize: "16px" }} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default StorageLeadList;
